// React hook that wraps `fetchRecentSentLeads` with the dock's
// fetch-on-expand pattern. Slice B2.3 of
// docs/planning/activity_dock_workflow_facilitation.md.
//
// Semantics:
//   - Fetches only when `enabled === true`. Sections that aren't
//     currently expanded should pass `enabled: false` so we don't pay
//     for the query when the user isn't looking.
//   - Refetches when `enabled` flips false → true, when `orgId`
//     changes, or when `days` changes.
//   - Caches the last successful result so a re-collapse → re-expand
//     during the same session displays cached data instantly while a
//     fresh query is in flight.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  fetchRecentSentLeads,
  type RecentSentLeadRow,
} from "@/lib/leads/fetchRecentSentLeads";

export type UseRecentSentLeadsState =
  | { status: "idle" }
  | { status: "loading"; rows: RecentSentLeadRow[] | null }
  | { status: "ready"; rows: RecentSentLeadRow[]; fetchedAt: number }
  | { status: "error"; error: string };

export type UseRecentSentLeadsArgs = {
  organizationId: string | null;
  /** Lookback window in days. Defaults to 30 in the lib. */
  days?: number;
  /** When false the hook stays idle. */
  enabled: boolean;
};

export type UseRecentSentLeadsResult = {
  state: UseRecentSentLeadsState;
  /** Manually re-run the query (e.g. a refresh button). */
  refresh: () => void;
};

export function useRecentSentLeads(
  args: UseRecentSentLeadsArgs,
): UseRecentSentLeadsResult {
  const { organizationId, days, enabled } = args;
  const [state, setState] = useState<UseRecentSentLeadsState>({
    status: "idle",
  });
  // Bump on manual refresh to retrigger the fetch effect without
  // changing organizationId / days.
  const [refreshTick, setRefreshTick] = useState(0);
  // Tracks the in-flight request so a late response from a stale
  // (orgId, days) combination can't clobber a newer result.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // Don't reset cached results on disable — the next enable will
      // refetch but in the meantime callers can keep showing the
      // previous render.
      return;
    }
    if (!organizationId) {
      setState({ status: "idle" });
      return;
    }
    const client = supabase;
    if (!client) {
      setState({
        status: "error",
        error: "Supabase client not available",
      });
      return;
    }

    const requestId = ++requestIdRef.current;
    setState((prev) => ({
      status: "loading",
      rows: prev.status === "ready" ? prev.rows : null,
    }));

    fetchRecentSentLeads({ client, organizationId, days })
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        if (result.ok) {
          setState({
            status: "ready",
            rows: result.rows,
            fetchedAt: Date.now(),
          });
        } else {
          setState({ status: "error", error: result.error });
        }
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        });
      });
  }, [enabled, organizationId, days, refreshTick]);

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  return { state, refresh };
}
